import { SPROUT_AGENT } from '@fruitful/llm-service-shared';
import { useRouter } from 'expo-router';
import { useMemo, useState } from 'react';
import {
    BadgeLabel,
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    H5Emphasized,
    H6Emphasized,
    Input,
    LabelRegular,
    MonoBodySmallRegular,
    ScrollView,
    SearchIcon,
    Spinner,
    XStack,
    YStack,
} from 'smoothie';

import { ExperimentAgentDropdown } from './ExperimentAgentDropdown';
import { type DatasetGroup, type DatasetView } from './loadDataset';
import { useDataset } from './useDataset';
import { useStartExperiment } from './useStartExperiment';

const titleFor = (agentId: string): string =>
    agentId === SPROUT_AGENT.id ? 'Sprout dataset' : 'Dataset';

const followUpLabel = (count: number): string =>
    count === 1 ? '1 follow-up' : `${String(count)} follow-ups`;

const caseCountLabel = (count: number): string =>
    count === 1 ? '1 case' : `${String(count)} cases`;

const matchesQuery = (
    groupName: string,
    evalCase: DatasetGroup['cases'][number],
    query: string,
): boolean => {
    const haystack = [
        groupName,
        evalCase.caseId,
        evalCase.prompt,
        evalCase.groundTruth,
        evalCase.fixtureId,
    ]
        .join('\n')
        .toLowerCase();
    return haystack.includes(query);
};

const filterGroups = (dataset: DatasetView, query: string): DatasetGroup[] => {
    const normalized = query.trim().toLowerCase();
    if (normalized === '') {
        return dataset.groups;
    }

    return dataset.groups.flatMap((group) => {
        const cases = group.cases.filter((evalCase) =>
            matchesQuery(group.name, evalCase, normalized),
        );
        return cases.length === 0 ? [] : [{ name: group.name, cases }];
    });
};

const countCases = (groups: DatasetGroup[]): number =>
    groups.reduce((total, group) => total + group.cases.length, 0);

const RunExperimentButton = ({
    agentId,
    targetAgentKey,
}: {
    agentId: string;
    targetAgentKey: string;
}) => {
    const { mutate: runDataset, isPending } = useStartExperiment(agentId);

    if (agentId !== SPROUT_AGENT.id) {
        return null;
    }

    return (
        <Button
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.SMALL}
            width="auto"
            text="Run experiment"
            disabled={isPending}
            icon={isPending ? <Spinner /> : undefined}
            onPress={() => {
                runDataset(targetAgentKey);
            }}
        />
    );
};

const DatasetHeader = ({
    agentId,
    dataset,
    visibleCaseCount,
    query,
    onQueryChange,
}: {
    agentId: string;
    dataset: DatasetView | null;
    visibleCaseCount: number;
    query: string;
    onQueryChange: (value: string) => void;
}) => {
    const router = useRouter();
    const [targetAgentKey, setTargetAgentKey] = useState<string>(
        SPROUT_AGENT.agentKey,
    );
    const totalCases = dataset == null ? 0 : countCases(dataset.groups);
    const isFiltering = query.trim() !== '' && dataset != null;

    return (
        <YStack
            backgroundColor="white"
            borderColor="$gray200"
            borderWidth={1}
            borderRadius={20}
            padding={20}
            gap={16}
        >
            <XStack
                justifyContent="space-between"
                alignItems="flex-start"
                gap={16}
            >
                <YStack gap={8} flex={1}>
                    <H5Emphasized>{titleFor(agentId)}</H5Emphasized>
                    {dataset != null ? (
                        <XStack gap={8} flexWrap="wrap" alignItems="center">
                            <BadgeLabel
                                variant="neutral"
                                size="regular"
                                text={dataset.datasetName}
                            />
                            <BadgeLabel
                                variant="neutral"
                                size="regular"
                                text={`Version ${String(dataset.version)}`}
                            />
                            <BadgeLabel
                                variant="gray"
                                size="regular"
                                text={
                                    isFiltering
                                        ? `${String(visibleCaseCount)} of ${caseCountLabel(totalCases)}`
                                        : caseCountLabel(totalCases)
                                }
                            />
                            <BadgeLabel
                                variant="gray"
                                size="regular"
                                text={
                                    dataset.groups.length === 1
                                        ? '1 group'
                                        : `${String(dataset.groups.length)} groups`
                                }
                            />
                        </XStack>
                    ) : null}
                </YStack>
                <XStack gap={8} alignItems="center">
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.SMALL}
                        width="auto"
                        text="Experiments"
                        onPress={() => {
                            router.push({
                                pathname: '/dataset/[agentId]/experiments',
                                params: { agentId },
                            });
                        }}
                    />
                    <RunExperimentButton
                        agentId={agentId}
                        targetAgentKey={targetAgentKey}
                    />
                    {agentId === SPROUT_AGENT.id ? (
                        <ExperimentAgentDropdown
                            agentKey={targetAgentKey}
                            onAgentKeyChange={setTargetAgentKey}
                        />
                    ) : null}
                </XStack>
            </XStack>
            {dataset != null ? (
                <Input
                    width="100%"
                    placeholder="Search cases"
                    leftIcon={SearchIcon}
                    value={query}
                    onChangeText={onQueryChange}
                />
            ) : null}
        </YStack>
    );
};

const caseDetail = (evalCase: DatasetGroup['cases'][number]): string | null => {
    const parts: string[] = [];
    if (evalCase.extraTurnCount > 0) {
        parts.push(followUpLabel(evalCase.extraTurnCount));
    }
    if (evalCase.fixtureId !== '' && evalCase.fixtureId !== 'default') {
        parts.push(`Fixture ${evalCase.fixtureId}`);
    }
    return parts.length === 0 ? null : parts.join(' · ');
};

const CaseCard = ({
    evalCase,
    isFirst,
}: {
    evalCase: DatasetGroup['cases'][number];
    isFirst: boolean;
}) => {
    const detail = caseDetail(evalCase);

    return (
        <YStack
            gap={10}
            padding={16}
            borderTopWidth={isFirst ? 0 : 1}
            borderTopColor="$gray200"
        >
            <XStack gap={16} alignItems="flex-start">
                <YStack flex={1} minWidth={0} gap={4}>
                    <BodyMediumEmphasized>
                        {evalCase.prompt}
                    </BodyMediumEmphasized>
                    {detail != null ? (
                        <BodySmallRegular color="$gray1000">
                            {detail}
                        </BodySmallRegular>
                    ) : null}
                </YStack>
                <YStack flexShrink={0} maxWidth="40%">
                    <MonoBodySmallRegular color="$gray1000" textAlign="right">
                        {evalCase.caseId}
                    </MonoBodySmallRegular>
                </YStack>
            </XStack>
            {evalCase.groundTruth !== '' ? (
                <YStack
                    gap={4}
                    padding={12}
                    borderRadius={12}
                    backgroundColor="$gray100"
                >
                    <LabelRegular color="$gray1000">Expected</LabelRegular>
                    <BodySmallRegular>{evalCase.groundTruth}</BodySmallRegular>
                </YStack>
            ) : null}
        </YStack>
    );
};

const GroupCard = ({ group }: { group: DatasetGroup }) => (
    <YStack gap={10}>
        <XStack alignItems="center" gap={10}>
            <YStack
                width={4}
                height={22}
                borderRadius={2}
                backgroundColor="$gray1200"
            />
            <H6Emphasized>{group.name}</H6Emphasized>
            <BodySmallRegular color="$gray1000">
                {caseCountLabel(group.cases.length)}
            </BodySmallRegular>
        </XStack>
        <YStack
            marginLeft={14}
            backgroundColor="white"
            borderColor="$gray400"
            borderWidth={1}
            borderRadius={16}
            overflow="hidden"
        >
            {group.cases.map((evalCase, index) => (
                <CaseCard
                    key={evalCase.id}
                    evalCase={evalCase}
                    isFirst={index === 0}
                />
            ))}
        </YStack>
    </YStack>
);

export const DatasetPage = ({ agentId }: { agentId: string }) => {
    const { dataset, isLoading, isError } = useDataset(agentId);
    const [query, setQuery] = useState('');
    const groups = useMemo(
        () => (dataset == null ? [] : filterGroups(dataset, query)),
        [dataset, query],
    );

    return (
        <YStack
            flex={1}
            minHeight={0}
            width="100%"
            height="100%"
            padding={16}
            gap={16}
        >
            <DatasetHeader
                agentId={agentId}
                dataset={dataset}
                visibleCaseCount={countCases(groups)}
                query={query}
                onQueryChange={setQuery}
            />
            {isLoading ? (
                <YStack flex={1} alignItems="center" justifyContent="center">
                    <Spinner />
                </YStack>
            ) : null}
            {isError ? (
                <YStack
                    backgroundColor="white"
                    borderColor="$gray200"
                    borderWidth={1}
                    borderRadius={20}
                    padding={20}
                >
                    <BodyMediumRegular>
                        Unable to load the dataset.
                    </BodyMediumRegular>
                </YStack>
            ) : null}
            {!isLoading && !isError && dataset == null ? (
                <YStack
                    backgroundColor="white"
                    borderColor="$gray200"
                    borderWidth={1}
                    borderRadius={20}
                    padding={20}
                >
                    <BodyMediumRegular>
                        No dataset is published for this agent.
                    </BodyMediumRegular>
                </YStack>
            ) : null}
            {!isLoading && !isError && dataset != null ? (
                <ScrollView flex={1}>
                    <YStack gap={32} paddingBottom={24}>
                        {groups.length === 0 ? (
                            <YStack
                                backgroundColor="white"
                                borderColor="$gray200"
                                borderWidth={1}
                                borderRadius={20}
                                padding={20}
                            >
                                <BodyMediumRegular>
                                    No cases match that search.
                                </BodyMediumRegular>
                            </YStack>
                        ) : (
                            groups.map((group) => (
                                <GroupCard key={group.name} group={group} />
                            ))
                        )}
                    </YStack>
                </ScrollView>
            ) : null}
        </YStack>
    );
};
